import {useDispatch, useSelector} from 'react-redux'
import {FieldArray, Formik} from 'formik'
import Input from '../../core/input'
import {SimpleBlueButton} from '../../core/button'
import {addResourceSchema} from '../../../utils/validation'
import {handleFormChanges, greaterThanOrEqualToZero, flattenErrors} from '../../../utils/forms'
import {parseAuthorizationHeader, extractHeaders} from '../../../utils/header'
import {getServices, setButtonIsDisabled, setShowLoader, updateResource} from '../../../stores/resources'
import ButtonLoader from '../../core/button/ButtonLoader'
import {useEffect, useMemo, useRef, useState} from 'react'
import CustomSelect from '../../core/customSelect'
import {locationManager} from '../../../utils/locations'
import ForPaid from '../../core/forPaid'
import LimitManager from '../../../utils/limitManager'
import types from '../../../data/monitoringTypes.json'
import {SecondaryTrashIcon, TrashIcon} from '../../../icons'
import Customer from '../../../utils/customer'
import httpMethods from '../../../data/httpMethods.json'
import Textarea from '../../core/textarea'
import TextHelper, {trimStringValues} from '../../../utils/text'
import protocols from '../../../data/protocols.json'
import authMethods from '../../../data/authMethods.json'
import parser from 'cron-parser'
import moment from 'moment'
import Modal from '../../core/modal'
import IconCardSlider from '../../core/slider/IconCardSlider'
import LargeSwitch from '../../core/switch/LargeSwitch'
import Tooltip from '../../core/tooltip'
import AuthMethods from './AuthMethods'
import {setSelectedTeamId} from '../../../stores/teams'
import regexPatterns from '../../../utils/regexPatterns'
import classNames from 'classnames'
import Checkbox from '../../core/checkbox'
import {getResourceRegions} from '../../../stores/monitoring'
import {colors} from '../../../utils/colors'
import {useConditionManagement, useFocusBlur} from './UseConditionsHook'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import localStorageManager from '../../../utils/localStorageManager'

const getAvailableOptions = (item, index, premiumText, usageLimitsData) => {
    if (index) {
        return {
            ...item,
            disabled: !LimitManager.getConditionMonitoringIsEnabled(usageLimitsData),
            label: LimitManager.getConditionMonitoringIsEnabled(usageLimitsData) ? item.label : `${item.label} ${premiumText}`
        }
    }
    return item
}

const isTypeEnabled = (resourceItem, usageLimitsData) => {
    const {monitoring_type: type} = resourceItem

    if (type === types.http.value && !resourceItem?.condition_value) {
        return true
    }

    if (type === types.icmp.value) {
        return LimitManager.getPingMonitoringIsEnabled(usageLimitsData)
    }

    if (type === types.port.value) {
        LimitManager.getPortMonitoringIsEnabled(usageLimitsData)
    }

    if (type === types.email_protocols.value) {
        return LimitManager.getEmailMonitoringIsEnabled(usageLimitsData)
    }

    return LimitManager.getConditionMonitoringIsEnabled(usageLimitsData)
}

const VALUES = 'values'
const HTTP = 'http'
const PORT = 'port'
const names = {
    conditions: 'conditions',
    type: 'type',
    monitoring_protocol: 'monitoring_protocol',
    name: 'name',
    service_id: 'service_id',
    url: 'url',
    regions: 'regions',
    is_advanced: 'is_advanced',
    headers: 'headers',
    conditionOption: 'conditionOption',
    skip_ssl_error: 'skip_ssl_error',
    not_follow_redirect: 'not_follow_redirect',
    keyword: 'keyword',
    team_id: 'team_id',
    method: 'method',
    port: 'port',
    data: 'data',
    body: 'body',
    key: 'key',
    operator: 'operator',
    value: 'value',
    grace_period: 'grace_period',
    recovery_period: 'recovery_period',
    maintenance_cron_expression: 'maintenance_cron_expression',
    maintenance_duration: 'maintenance_duration'
}

const MIN_CONDITIONS_LENGTH = 1

const EditResourceModal = ({resource, setClickedResource, isOpen, title, closeModal, setIsEdited, setSelectRegions}) => {
    const text = useNamespaceTranslations('resources')
    const data = useNamespaceTranslations('resourcesData')
    const conditionTypes =  useNamespaceTranslations('condition-types')?.resource_conditions
    const usageLimitsData =  useNamespaceTranslations('usage-limits')

    const defaultConditionOptions = useMemo(() => Object.values(conditionTypes), [conditionTypes])
    const JSON_VALUES = useMemo(() => Object.values(conditionTypes?.json_key_value_check?.values), [conditionTypes])
    const KEYWORD_VALUES = useMemo(() => Object.values(conditionTypes?.keyword_value_check?.values), [conditionTypes])
    const STATUS_CODE_VALUES = useMemo(() => Object.values(conditionTypes?.status_code_check?.values), [conditionTypes])
    const gracePeriodValues = useMemo(() => Object.values(text.grace_periods), [text.grace_periods])
    const recoveryPeriodValues = useMemo(() => Object.values(text.recovery_periods), [text.recovery_periods])
    const resourcesDataValues = useMemo(() => Object.values(data.resource_types), [data.resource_types])

    const conditionPairs = {
        [conditionTypes?.json_key_value_check?.type]: [{key: '', operator: '', value: ''}],
        [conditionTypes?.keyword_value_check?.type]: [{key: '', operator: '', value: ''}],
        [conditionTypes?.status_code_check?.type]: [{key: '', operator: '', value: ''}]
    }

    const conditionOptions = useMemo(() => defaultConditionOptions?.map((item, index) => getAvailableOptions(item, index, text.premium, usageLimitsData)), [])
    const [defaultConditionOption] = conditionOptions
    const buttonIsDisabled = useSelector(state => state.resources.buttonIsDisabled)
    const showLoader = useSelector(state => state.resources.showLoader)
    const currentTeamId = useSelector(state => state.teams.selectedTeamId)
    const isDarkMode = useSelector(state => state.sidebar.isDarkMode)
    const [teamServices, setTeamServices] = useState([])

    const services = useMemo(() => {
        return localStorageManager.get('services') || '[]'
    }, [])
    const dispatch = useDispatch()
    const [active, setActive] = useState(null)
    const defaultHeaders = {key: '', value: ''}
    const teams = useSelector(state => state.teams.teams)
    const [nextTriggerDate, setNextTriggerDate] = useState('')
    const [cronInputIsValid, setCronInputIsValid] = useState(true)
    const hasAdminAccess = Customer.isRoot() || Customer.isManager()
    const containerRef = useRef(null)
    const formikRef = useRef(null)
    const defaultActiveResourceKey = Object.keys(protocols.smtp_imap_pop3.data).includes(resource?.type) ?
        data.resourceKeys.mail_server : resource?.type === data.resource_types.uptime.monitoring_type ?
            data.resourceKeys.uptime : resource?.type
    const currentConditionType = resource?.conditions?.[0]?.type || null
    const [activeResourceKey, setActiveResourceKey] = useState(
        resourcesDataValues?.find(
            resourceItem => !resourceItem?.disabled && resourceItem.monitoring_type === resource?.type
                    && resourceItem?.condition_type === currentConditionType
        )?.key || defaultActiveResourceKey
    )

    const authorizationHeader = extractHeaders(resource).authorizationHeader
    const remainingHeaders = extractHeaders(resource).remainingHeaders

    const headers = resource?.headers
        ?
        Object.entries(remainingHeaders).map(([key, value]) => ({key, value}))
        :
        [defaultHeaders]

    const serviceId = services?.find(service => service?.value === resource?.service_id)

    const initialValues = {
        type: resource?.type,
        monitoring_protocol: Object.values(types).filter(item => item?.monitoring_protocol).find(item => item?.value === resource?.type),
        name: resource?.name,
        service_id: serviceId,
        url: resource?.type === types.http.value ? resource?.url : resource?.host,
        regions: locationManager.getMainLocations().filter(item => resource?.regions?.includes(item.value)),
        is_advanced: Boolean(resource?.is_advanced),
        headers: headers,
        team_id: teams.find(item => item.value === currentTeamId),
        conditions: conditionPairs,
        skip_ssl_error: resource?.settings?.skip_ssl_error,
        not_follow_redirect: resource?.settings?.not_follow_redirect,
        auth_method: authMethods[parseAuthorizationHeader(authorizationHeader?.Authorization)?.type],
        bearer_token: parseAuthorizationHeader(authorizationHeader?.Authorization)?.token,
        username: parseAuthorizationHeader(authorizationHeader?.Authorization)?.username,
        password: parseAuthorizationHeader(authorizationHeader?.Authorization)?.password,
        method: httpMethods[resource?.method],
        port: resource?.port,
        data: resource?.data,
        body: resource?.body,
        grace_period:  greaterThanOrEqualToZero(resource?.settings?.period?.grace) ? gracePeriodValues.find(item => item?.value === resource?.settings?.period?.grace) : null,
        recovery_period:  greaterThanOrEqualToZero(resource?.settings?.period?.recovery) ? recoveryPeriodValues.find(item => item?.value === resource?.settings?.period?.recovery) : null,
        maintenance_cron_expression: resource?.settings?.maintenance?.cron_expression,
        maintenance_duration: resource?.settings?.maintenance?.duration
    }

    const {focusedInput, handleFocus, handleBlur} = useFocusBlur()
    const {handleConditionsInputChange, handleRemoveConditions, submitted, setSubmitted, keyCounts} = useConditionManagement(formikRef)

    const [initialFormValues, setInitialFormValues] = useState(initialValues)
    const dateTimeUTCString = 'YYYY-MM-DD HH:mm:ss UTC'

    const setConditionState = (resource, conditionOptions) => {
        const resourceConditions = resource?.conditions
        if (!Array.isArray(resourceConditions)) {
            setInitialFormValues(prevState => ({
                ...prevState,
                conditionOption: defaultConditionOption
            }))
            return
        }
        const [condition] = resourceConditions
        if (condition?.type === conditionTypes?.does_contain?.type && [types.tcp.value, types.udp.value].includes(resource?.type)) {
            const resourceConditionOption = conditionOptions.find(item => item.value === condition.operator)
            const resourceKeywords = condition.values.join(' ')
            setInitialFormValues(prevState => ({
                ...prevState,
                conditionOption: resourceConditionOption,
                keyword: resourceKeywords,
                conditions: {}
            }))

            return
        }
        const conditionOption = defaultConditionOptions?.find(item => item?.type === condition?.type && !item?.isCondition)
        const updatedConditions = resource.conditions.map(condition => (
            {
                key: condition?.key,
                value: condition?.values?.join(' '),
                operator: condition?.type === conditionTypes?.json_key_value_check?.type ?
                    JSON_VALUES?.find(item => item?.value === condition?.operator) :
                    condition?.type === conditionTypes?.status_code_check?.type ? STATUS_CODE_VALUES.find(item => item?.value === condition?.operator) :
                        KEYWORD_VALUES.find(item => item?.value === condition?.operator)
            }
        ))
        setInitialFormValues(prevState => ({
            ...prevState,
            conditionOption,
            conditions: {...initialFormValues.conditions, [conditionOption.type]: updatedConditions}
        }))
    }

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const userUpdateResource = async(values, {setFieldError}) => {
        const conditionType = values?.conditionOption?.type
        const updatedConditions = values?.conditions?.[conditionType]?.length > MIN_CONDITIONS_LENGTH ? values?.conditions?.[conditionType]?.filter(condition => condition?.key && condition?.value) : values?.conditions?.[conditionType]
        let formData = {
            ...values,
            conditions: updatedConditions?.map(conditions => trimStringValues(conditions)),
            regions: values.regions.map(item => item.value),
            service_id: values.service_id?.value,
            team_id: values?.team_id?.value,
            method: values?.method?.value,
            port: values?.port ? String(values?.port) : null,
            grace_period: values?.grace_period?.value,
            recovery_period: values?.recovery_period?.value
        }
        if (values.is_advanced) {
            const headers = {}
            values.headers?.forEach(item => {
                if (item?.key && item?.value) {
                    headers[item.key] = item.value
                }
            })
            if (values.auth_method?.value === authMethods.bearer_token.value) {
                headers['Authorization'] = `Bearer ${values.bearer_token}`
            }
            if (values.auth_method?.value === authMethods.basic_auth.value) {
                headers['Authorization'] = `Basic ${btoa(values.username + ':' + values.password)}`
            }
            formData = {
                ...formData,
                headers: JSON.stringify(headers),
                maintenance_cron_expression: values?.maintenance_cron_expression,
                maintenance_duration: parseInt(values?.maintenance_duration)
            }
        }
        if (!values.is_advanced) {
            formData = {
                ...formData,
                headers: null,
                maintenance_cron_expression: null,
                maintenance_duration: null
            }
        }
        delete formData.auth_method
        delete formData.bearer_token
        delete formData.username
        delete formData.password
        const success = await dispatch(updateResource(resource?.resource_id, trimStringValues(formData), setFieldError))
        setSubmitted(formData?.conditions || [])
        if (success) {
            typeof setClickedResource === 'function' && setClickedResource(null)
            setSelectRegions(await dispatch(getResourceRegions(resource?.resource_id, null)))
        }
    }

    const getInfoBox = regions => {
        return regions?.length === LimitManager.getRegionLimit(usageLimitsData) && regions?.length !== locationManager.getMainLocations().length && (
            <ForPaid className='as-form-element' forPaidText={text.for_paid_region}/>
        )
    }

    const checkInputs = (headers, push) => {
        const hasKey = headers.every(item => item?.key)
        const hasValue = headers.every(item => item?.value)
        if (hasKey || hasValue) {
            push(defaultHeaders)
        }
    }

    const removeHeader = (remove, index) => {
        remove(index)
        dispatch(setButtonIsDisabled(false))
    }

    let headerPusher
    const generateJSONFields = (values, setFieldValue, errors) => {
        const headers = values.headers?.length ? values.headers : [defaultHeaders]

        return <FieldArray name={names.headers}>
            {({push, remove}) => {
                headerPusher = push
                return (
                    headers?.map((item, index) => (
                        <div className={classNames('json-box', {
                            dark: isDarkMode
                        })} key={index}>
                            <div className="json-box-header">
                                <span className="title">{text.header}</span>
                                {index > 0 && <span className={classNames('delete', {
                                    dark: isDarkMode
                                })} onClick={() => removeHeader(remove, index)}>
                                    <TrashIcon/>
                                </span>}
                            </div>
                            <div className="json-box-content simple-headers">
                                <Input
                                    onChange={e => {
                                        setFieldValue(e)
                                        checkInputs(headers, push)
                                    }}
                                    error={errors?.headers?.[index]?.key}
                                    value={item.key}
                                    name={`headers.${index}.key`}
                                    placeholder={text.key}
                                    inputIsDisabled={!values[names.is_advanced]}
                                />
                                <Input
                                    onChange={e => {
                                        setFieldValue(e)
                                        checkInputs(headers, push)
                                    }}
                                    error={errors?.headers?.[index]?.value}
                                    value={item.value}
                                    name={`headers.${index}.value`}
                                    placeholder={text.value}
                                    inputIsDisabled={!values[names.is_advanced]}
                                />
                            </div>
                        </div>
                    ))
                )
            }}
        </FieldArray>
    }

    const handleCronInputChange = (e, handleChange) => {
        handleChange(e)
        const interval = parser.parseExpression(e.target.value)
        if (regexPatterns.cron.test(e.target.value)) {
            setNextTriggerDate(moment(interval.next().toString()).format(dateTimeUTCString))
            setCronInputIsValid(true)
            return
        }
        setNextTriggerDate('')
        setCronInputIsValid(false)
    }

    const generateMaintenanceField = (values, handleChange, errors) => {
        return (
            <div className={classNames('json-box', {
                dark: isDarkMode
            })}>
                <div className="json-box-header">
                    <span className="title">{text.maintenance}</span>
                </div>
                <div className="json-box-content maintenance">
                    <Input
                        onChange={e => {
                            handleCronInputChange(e, handleChange)
                        }}
                        error={errors?.maintenance_cron_expression}
                        name={names.maintenance_cron_expression}
                        placeholder={text.cron_expression}
                        value={values.maintenance_cron_expression}
                        handleInfo={(state) => handleInfo(names.maintenance_cron_expression, state)}
                        infoText={text.cron_expression_info}
                        isValid={cronInputIsValid}
                        inputIsDisabled={!values[names.is_advanced]}
                    />
                    {nextTriggerDate && (
                        <span className={classNames('next', {
                            dark: isDarkMode
                        })}>{`${text.next} ${nextTriggerDate}`}</span>
                    )}
                    <Input
                        onChange={handleChange}
                        error={errors?.maintenance_duration}
                        name={names.maintenance_duration}
                        placeholder={text.duration}
                        value={values.maintenance_duration}
                        handleInfo={(state) => handleInfo(names.maintenance_duration, state)}
                        infoText={text.duration_info}
                        inputIsDisabled={!values[names.is_advanced]}
                    />
                </div>
            </div>
        )
    }

    const handleChangeBody = (e, handleChange, setValue, values) => {
        dispatch(setButtonIsDisabled(handleFormChanges(e, names.body, setValue, initialFormValues)))
        const hasContentType = values?.headers?.some(item => item?.value === data.content_type.values.appliaction_json.key && item?.key === data.content_type.key)
        if (hasContentType) {
            return
        }
        try {
            JSON.parse(e.target.value)
            let emptyHeaderIndex = values?.headers?.findIndex(item => !item?.value && !item?.key)
            emptyHeaderIndex = emptyHeaderIndex > -1 ? emptyHeaderIndex : 0
            setValue(`headers.${emptyHeaderIndex}.key`, data.content_type.key)
            setValue(`headers.${emptyHeaderIndex}.value`, data.content_type.values.appliaction_json.key)
            headerPusher(defaultHeaders)
        } catch (e) {
            return false
        }
    }

    const generateMethods = (values, handleChange, setFieldValue, errors) => {
        return (
            <>
                <CustomSelect
                    options={Object.values(httpMethods)}
                    onChange={(name, data) => {
                        setFieldValue(name, data)
                    }}
                    placeholder={text.method}
                    value={values.method}
                    error={errors.method}
                    name={names.method}
                    activeInfo={active === names.method}
                    handleInfo={(state) => handleInfo(names.method, state)}
                    infoText={text.method_info}
                    isDisabled={!values[names.is_advanced]}
                />
                {values.method?.hasPayload && (
                    <Textarea
                        onChange={e => handleChangeBody(e, handleChange, setFieldValue, values)}
                        value={values.body}
                        name={names.body}
                        error={errors.body}
                        placeholder={text.body}
                        activeInfo={active === names.body}
                        handleInfo={(state) => handleInfo(names.body, state)}
                        infoText={text.body_info}
                        isDisabled={!values[names.is_advanced]}
                    />
                )}
            </>
        )
    }

    const generateKeywordFields = (values, errors) => {
        const {values: options, type} = conditionTypes.keyword_value_check

        return (
            <div className='checker-wrapper'>
                {values.conditions?.[type].map((condition, index) =>
                    <>
                        <Input
                            type="text"
                            value=""
                            placeholder={text.keyword}
                            name={names.key}
                            error={errors?.conditions?.[type]?.[index]?.key}
                            inputIsDisabled
                            activeInfo={active === names.key}
                            handleInfo={(state) => handleInfo(names.key, state)}
                        />
                        <CustomSelect
                            options={Object.values(options)}
                            name={names.operator}
                            placeholder={text.operator}
                            onChange={(_, data) => handleConditionsInputChange(index, names.operator, data)}
                            isClearable
                            error={errors?.conditions?.[type]?.[index]?.operator || errors?.[type]}
                            value={condition.operator}
                            activeInfo={active === names.operator}
                            handleInfo={(state) => handleInfo(names.operator, state)}
                        />
                        <Input
                            type="text"
                            value={condition.value}
                            infoText={TextHelper.replaceAllChars(text.value_info, ':val', text.value_lowercase)}
                            placeholder={text.value}
                            name={names.value}
                            error={errors?.conditions?.[type]?.[index]?.value}
                            onChange={(e) => handleConditionsInputChange(index, names.value, e.target.value)}
                            activeInfo={active === names.value}
                            handleInfo={(state) => handleInfo(names.value, state)}
                        />
                    </>
                )}
            </div>
        )
    }

    const generateStatusCodeFields = (values, errors) => {
        const {values: options, type} = conditionTypes.status_code_check

        return (
            <div className='checker-wrapper'>
                {values.conditions?.[type].map((condition, index) =>
                    <>
                        <Input
                            type="text"
                            value=""
                            placeholder={text.status_code_name}
                            name={names.key}
                            error={errors?.conditions?.[type]?.[index]?.key}
                            inputIsDisabled
                            activeInfo={active === names.key}
                            handleInfo={(state) => handleInfo(names.key, state)}
                        />
                        <CustomSelect
                            options={Object.values(options)}
                            name={names.operator}
                            placeholder={text.operator}
                            onChange={(_, data) => handleConditionsInputChange(index, names.operator, data)}
                            isClearable
                            error={errors?.conditions?.[type]?.[index]?.operator || errors?.[type]}
                            value={condition.operator}
                            activeInfo={active === names.operator}
                            handleInfo={(state) => handleInfo(names.operator, state)}
                        />
                        <Input
                            type="text"
                            value={condition.value}
                            infoText={text.status_code_info}
                            placeholder={text.status_code}
                            name={names.value}
                            error={errors?.conditions?.[type]?.[index]?.value}
                            onChange={(e) => handleConditionsInputChange(index, names.value, e.target.value)}
                            activeInfo={active === names.value}
                            handleInfo={(state) => handleInfo(names.value, state)}
                        />
                    </>
                )}
            </div>
        )
    }

    const generateStandardJsonFields = (values, errors) => {
        const {values: options, type} = conditionTypes.json_key_value_check
        const conditions = values?.conditions?.[type]

        const modifiedErrors = errors?.conditions?.[type] ? errors : flattenErrors(errors)

        return (
            <>
                {conditions.map((condition, index) => {
                    const isFocused = name => focusedInput?.[index] === name
                    const errorIndex = errors?.conditions?.[type] ? index : conditions.slice(0, index).filter(c => c.key === condition.key).length
                    const foundCondition = submitted?.find((_, i) => i === index)
                    const isSameLength = modifiedErrors?.[foundCondition?.key]?.length === keyCounts[foundCondition?.key]

                    return <div className={`checker-wrapper ${focusedInput?.[index] ? 'focused-container' : ''}`}>
                        <Input
                            type="text"
                            value={condition?.key}
                            placeholder={text.key}
                            infoText={index === 0 && isFocused(names.key) && text.json_info}
                            customIcon={index !== 0 && isFocused(names.key) && <SecondaryTrashIcon />}
                            name={names.key}
                            containerClassName={!isFocused(names.key) ? 'no-focused' : ''}
                            error={modifiedErrors?.conditions?.[type]?.[errorIndex]?.key}
                            onChange={(e) => handleConditionsInputChange(index, names.key, e.target.value, true)}
                            activeInfo={active === names.key}
                            handleInfo={(state) => handleInfo(names.key, state)}
                            onFocus={() => handleFocus(names.key, index)}
                            onBlur={handleBlur}
                        />
                        <CustomSelect
                            options={Object.values(options)}
                            name={names.operator}
                            placeholder={text.operator}
                            onChange={(_, data) => handleConditionsInputChange(index, names.operator, data, true)}
                            isClearable
                            error={modifiedErrors?.conditions?.[type]?.[errorIndex]?.operator || (isSameLength && modifiedErrors?.[foundCondition?.key]?.[errorIndex])}
                            value={condition?.operator}
                            activeInfo={active === names.operator}
                            handleInfo={(state) => handleInfo(names.operator, state)}
                        />
                        <Input
                            type="text"
                            value={condition?.value}
                            containerClassName={!isFocused(names.value) ? 'no-focused' : ''}
                            infoText={index === 0 && text.json_info}
                            customIcon={index !== 0 && <SecondaryTrashIcon stroke={colors.colorRed}/>}
                            customIconOnclick={() => handleRemoveConditions(condition, errorIndex, index, modifiedErrors)}
                            placeholder={text.value}
                            name={names.value}
                            error={modifiedErrors?.conditions?.[type]?.[errorIndex]?.value}
                            onChange={(e) => handleConditionsInputChange(index, names.value, e.target.value, true)}
                            activeInfo={active === names.value}
                            handleInfo={(state) => handleInfo(names.value, state)}
                            onFocus={() => handleFocus(names.value, index)}
                            onBlur={handleBlur}
                        />
                    </div>
                }
                )}
            </>
        )
    }

    const getCurrentFields = (values, errors) => {
        if (values.conditionOption?.type === conditionTypes?.keyword_value_check?.value) {
            return generateKeywordFields(values, errors)
        }
        if (values.conditionOption?.type === conditionTypes?.status_code_check?.value) {
            return generateStatusCodeFields(values, errors)
        }
        if (values.conditionOption?.type === conditionTypes?.json_key_value_check?.value) {
            return generateStandardJsonFields(values, errors)
        }
    }

    const getConditionOptions = type => {
        if ([types.udp.value, types.tcp.value].includes(type)) {
            return [conditionTypes?.becomes_unavailable, conditionTypes?.does_contain, conditionTypes?.does_not_contain]
        }
        return conditionOptions
    }

    const hasPort = values => [types.udp.value, types.tcp.value, types.pop.value, types.imap.value, types.smtp.value].includes(values[names.type])
    const isHttpMonitoring = values => [types.http.value].includes(values[names.type])
    const isPortMonitoring = values => [types.udp.value, types.tcp.value].includes(values.type)

    useEffect(() => {
        if (initialFormValues.headers?.some(item => (item.value)) && resource?.id) {
            setInitialFormValues(prev => ({
                ...prev,
                headers: [...prev.headers, defaultHeaders]
            }))
        }
        setConditionState(resource, conditionOptions)
    }, [resource?.id])

    useEffect(() => {
        if (services.length) {
            setTeamServices(services)
            setInitialFormValues(prev => ({
                ...prev,
                service_id: serviceId
            }))
        }
    }, [services])

    useEffect(() => {
        setIsEdited && setIsEdited(true)
        return () => dispatch(setShowLoader(false))
    }, [])

    const handleChangeResource = (monitoringType, conditionType, resourceKey, setFieldValue) => {
        const {values} = formikRef.current
        const monitoringTypes = Object.values(types).filter(monitoringTypeItem => !monitoringTypeItem?.monitoring_protocol)
        const currentMonitoringType = monitoringTypes.find(monitoringTypeItem => monitoringTypeItem.value === monitoringType)
        const currentConditionOption = conditionOptions.find(conditionTypeItem => conditionTypeItem.value === conditionType)
        if (currentConditionOption) {
            dispatch(setButtonIsDisabled(handleFormChanges(currentConditionOption, names.conditionOption, setFieldValue, initialFormValues)))
            const updatedConditions = values?.conditions?.[currentConditionOption.type]?.map(conditionPair => ({...conditionPair,
                operator: !conditionPair.operator ?
                    Object.values(currentConditionOption?.values)?.[0] :
                    conditionPair.operator
            }))
            setFieldValue(names.conditions, {...values.conditions, [currentConditionOption.type]: updatedConditions})
        }

        if (currentMonitoringType?.value === types.port.value) {
            if (resourceKey === protocols.port.data.udp.value) {
                dispatch(setButtonIsDisabled(handleFormChanges(protocols.port.data.udp, names.monitoring_protocol, setFieldValue, initialFormValues)))
            }
            if (resourceKey === protocols.port.data.tcp.value) {
                dispatch(setButtonIsDisabled(handleFormChanges(protocols.port.data.tcp, names.monitoring_protocol, setFieldValue, initialFormValues)))
            }
        }
        setActiveResourceKey(resourceKey)
    }

    return (
        <Modal
            contentClassName="resource-modal-content"
            title={title}
            isOpen={isOpen}
            closeModal={closeModal}>
            <div className='resource-modal-body' ref={containerRef}>
                <div className="modal-title">{text.resource_types}</div>
                <Formik
                    enableReinitialize={true}
                    initialValues={initialFormValues}
                    onSubmit={userUpdateResource}
                    validationSchema={() => addResourceSchema(conditionTypes)}
                    validateOnBlur={false}
                    innerRef={formikRef}
                    validateOnChange={false}>
                    {({values, errors, handleChange, handleSubmit, setFieldValue, setFieldError}) => {
                        return (
                            <>
                                <IconCardSlider
                                    slides={
                                        resourcesDataValues
                                            ?.filter(data => !data.disabled)
                                            ?.map(resourceItem => {
                                                const isActive = resourceItem.key === activeResourceKey
                                                const isAvailableResource = resourceItem?.monitoring_type !== PORT ? resourceItem.monitoring_type === resource.type : resourceItem.key === resource.type
                                                return {
                                                    ...resourceItem,
                                                    isDiffType: isTypeEnabled(resourceItem, usageLimitsData) && !isAvailableResource,
                                                    disabled: !isTypeEnabled(resourceItem, usageLimitsData) || (isTypeEnabled(resourceItem, usageLimitsData) && !isAvailableResource && !isActive),
                                                    icon: resourceItem.icon,
                                                    active: isActive,
                                                    onClick: () => handleChangeResource(resourceItem.monitoring_type, resourceItem.condition_type, resourceItem.key, setFieldValue)
                                                }
                                            })
                                    }
                                />
                                <div className="modal-title">{text.resource_details}</div>
                                <form onSubmit={handleSubmit} className="modal-body">
                                    <div className='form-container'>
                                        <div className="standard-form">
                                            <Input
                                                type="text"
                                                value={values.url}
                                                infoText={text.url_info}
                                                placeholder={values[names.type] === types.icmp.value ? text.host : text.url}
                                                name={names.url}
                                                error={errors.url}
                                                inputIsDisabled={true}
                                                activeInfo={active === names.url}
                                                handleInfo={(state) => handleInfo(names.url, state)}
                                            />
                                            {values.type === HTTP &&
                                            <>
                                                <Checkbox
                                                    type="checkbox"
                                                    id="skip-ssl-error-checkbox"
                                                    checked={!values.skip_ssl_error}
                                                    infoText={text.skip_ssl_validation_info}
                                                    label={text.skip_ssl_validation_label}
                                                    onChange={() => setFieldValue(names.skip_ssl_error, !values.skip_ssl_error)}
                                                />
                                                <Checkbox
                                                    type="checkbox"
                                                    disabled
                                                    readOnly
                                                    id="follow-redirect-checkbox"
                                                    checked={!values.not_follow_redirect}
                                                    infoText={text.follow_redirect_info}
                                                    label={text.follow_redirect_label}
                                                />
                                            </>
                                            }
                                            {[types.tcp.value, types.udp.value].includes(values?.type) &&
                                            values.conditionOption?.value !== data.resource_types.uptime.condition_type &&
                                                <Input
                                                    type="text"
                                                    value={values.keyword}
                                                    placeholder={text.keyword}
                                                    name={names.keyword}
                                                    error={errors.keyword}
                                                    infoText={values.conditionOption?.inputInfoText}
                                                    onChange={handleChange}
                                                    activeInfo={active === names.keyword}
                                                    handleInfo={(state) => handleInfo(names.keyword, state)}
                                                />}
                                            {[types.tcp.value, types.udp.value].includes(values?.type) &&
                                                <CustomSelect
                                                    options={getConditionOptions(values?.type)}
                                                    value={values.conditionOption}
                                                    infoText={values?.conditionOption?.selectInfoText}
                                                    placeholder={text.condition_option}
                                                    name={names.conditionOption}
                                                    error={!values?.conditionOption?.value && errors.conditions}
                                                    onChange={(name, value) => {
                                                        setFieldValue(name, value)
                                                        setFieldError(names.keyword, null)
                                                        setFieldError(names.conditions, null)
                                                        setFieldValue(names.operator, null)
                                                        setFieldValue(names.key, '')
                                                        setFieldValue(names.value, '')
                                                    }}

                                                    activeInfo={active === names.conditionOption}
                                                    handleInfo={(state) => handleInfo(names.conditionOption, state)}
                                                />}
                                            {hasPort(values) && (
                                                <>
                                                    {Object.keys(protocols.smtp_imap_pop3.data).includes(values.type) &&
                                                        <Tooltip
                                                            trigger="mouseenter"
                                                            placement="top"
                                                            content={text.cannot_be_change}
                                                            appendTo={containerRef?.current}
                                                        >
                                                            <CustomSelect
                                                                options={Object.values(protocols[isPortMonitoring(values) ? protocols.port.key : protocols.smtp_imap_pop3.key].data)}
                                                                value={values.monitoring_protocol}
                                                                infoText={isPortMonitoring(values) ? text.protocol_info : text.email_protocol_info}
                                                                placeholder={text.select_protocol}
                                                                name={names.monitoring_protocol}
                                                                error={errors.monitoring_protocol}
                                                                onChange={setFieldValue}
                                                                isDisabled
                                                                activeInfo={active === names.monitoring_protocol}
                                                                handleInfo={(state) => handleInfo(names.monitoring_protocol, state)}
                                                            />
                                                        </Tooltip>
                                                    }
                                                    <Input
                                                        type="number"
                                                        value={values.port}
                                                        placeholder={text.port}
                                                        infoText={text.port_info}
                                                        name={names.port}
                                                        error={errors.port}
                                                        onChange={handleChange}
                                                        activeInfo={active === names.port}
                                                        handleInfo={(state) => handleInfo(names.port, state)}
                                                    />
                                                    {isPortMonitoring(values) && (
                                                        <Textarea
                                                            value={values.data}
                                                            placeholder={text.data}
                                                            infoText={text.data_info}
                                                            name={names.data}
                                                            error={errors.data}
                                                            onChange={handleChange}
                                                            activeInfo={active === names.data}
                                                            handleInfo={(state) => handleInfo(names.data, state)}
                                                        />
                                                    )}
                                                </>
                                            )}
                                            {values.conditionOption && VALUES in values.conditionOption && getCurrentFields(values, errors)}
                                            <Input
                                                type="text"
                                                value={values.name}
                                                infoText={text.resource_name_info}
                                                placeholder={text.resource_name}
                                                name={names.name}
                                                error={errors.name}
                                                activeInfo={active === names.name}
                                                handleInfo={(state) => handleInfo(names.name, state)}
                                                onChange={handleChange}
                                            />
                                            {hasAdminAccess && teams.length > 0 && (
                                                <CustomSelect
                                                    options={teams}
                                                    name={names.team_id}
                                                    placeholder={text.team_name}
                                                    onChange={async(name, data) => {
                                                        setFieldValue(name, data)
                                                        dispatch(setSelectedTeamId(data.value))
                                                        const services = await dispatch(getServices(false, true))
                                                        data?.value && setFieldValue(names.service_id, services[0])
                                                        setTeamServices(services)
                                                    }}
                                                    isClearable
                                                    error={errors.team_id}
                                                    value={values.team_id}
                                                    activeInfo={active === names.team_id}
                                                    infoText={text.team_name_info}
                                                    handleInfo={(state) => handleInfo(names.team_id, state)}
                                                />
                                            )}
                                            <CustomSelect
                                                options={teamServices}
                                                name={names.service_id}
                                                placeholder={text.service_name}
                                                onChange={setFieldValue}
                                                isClearable
                                                error={errors.service_id}
                                                value={values.service_id}
                                                activeInfo={active === names.service_id}
                                                infoText={text.service_name_info}
                                                handleInfo={(state) => handleInfo(names.service_id, state)}
                                            />
                                            <CustomSelect
                                                isMulti
                                                showLabel
                                                placeholderAsLabel={false}
                                                name={names.regions}
                                                className="regions"
                                                options={locationManager.getMainLocations()}
                                                placeholder={text.regions}
                                                onChange={setFieldValue}
                                                value={values.regions}
                                                error={errors.regions}
                                                infoText={text.regions_info}
                                                limit={LimitManager.getRegionLimit(usageLimitsData)}
                                            />
                                            {getInfoBox(values?.regions)}
                                        </div>
                                        <div className="advanced-form">
                                            <div className='advanced-checkbox-wrapper'>
                                                <LargeSwitch
                                                    onChange={e => {
                                                        setFieldValue(names.headers, [defaultHeaders])
                                                        setFieldError(names.headers, null)
                                                        setFieldValue(names.body, '')
                                                        dispatch(setButtonIsDisabled(handleFormChanges(e.target.checked, names.is_advanced, setFieldValue, initialFormValues, true)))
                                                    }}
                                                    checked={!!values[names.is_advanced]}
                                                    name={names.is_advanced}
                                                    label={text.use_advanced}
                                                />
                                                <div className="advanced-checkbox-label">
                                                    {text.use_advanced}
                                                </div>
                                            </div>
                                            <CustomSelect
                                                name={names.grace_period}
                                                options={gracePeriodValues}
                                                placeholder={text.grace_period}
                                                onChange={setFieldValue}
                                                value={values.grace_period}
                                                error={errors.grace_period}
                                                infoText={text.grace_period_info}
                                                activeInfo={active === names.grace_period}
                                                handleInfo={(state) => handleInfo(names.grace_period, state)}
                                                isDisabled={!values[names.is_advanced]}
                                            />
                                            <CustomSelect
                                                name={names.recovery_period}
                                                options={recoveryPeriodValues}
                                                placeholder={text.recovery_period}
                                                onChange={setFieldValue}
                                                value={values.recovery_period}
                                                error={errors.recovery_period}
                                                infoText={text.recovery_period_info}
                                                activeInfo={active === names.recovery_period}
                                                handleInfo={(state) => handleInfo(names.recovery_period, state)}
                                                isDisabled={!values[names.is_advanced]}
                                            />
                                            {isHttpMonitoring(values) && (
                                                <>
                                                    {generateMethods(values, handleChange, setFieldValue, errors)}
                                                    <AuthMethods
                                                        values={values}
                                                        handleChange={handleChange}
                                                        setFieldValue={setFieldValue}
                                                        errors={errors}
                                                        handleInfo={handleInfo}
                                                        active={active}
                                                    />
                                                    {generateJSONFields(values, handleChange, errors)}
                                                </>
                                            )}
                                            {generateMaintenanceField(values, handleChange, errors)}
                                        </div>
                                    </div>
                                    <div className='submit-button-wrapper'>
                                        <SimpleBlueButton
                                            disabled={buttonIsDisabled || showLoader}
                                            type="submit"
                                        >
                                            {showLoader && <ButtonLoader/>}
                                            <span>{text.update_resource}</span>
                                        </SimpleBlueButton>
                                    </div>
                                </form>
                            </>
                        )
                    }
                    }
                </Formik>
            </div>
        </Modal>
    )
}

export default EditResourceModal
